import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Users, Loader2, ArrowLeft, Tent, Zap, Bot, ArrowBigUp, ArrowBigDown, MessageSquare, Flame, TrendingUp, Clock, LayoutList, Newspaper } from "lucide-react";
import { api } from "@/context/AuthContext";
import { PostCard } from "@/components/dashboard/CyberWall";

const timeAgo = (iso) => {
  const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
};

const VoteColumn = ({ score, myVote, onVote, small = false }) => (
  <div className={`flex flex-col items-center shrink-0 ${small ? "gap-0" : "gap-0.5"}`}>
    <button
      onClick={(e) => { e.stopPropagation(); onVote(myVote === "up" ? "clear" : "up"); }}
      className={`p-0.5 rounded transition-colors ${myVote === "up" ? "text-[#00FFAA]" : "text-white/30 hover:text-[#00FFAA]"}`}
      data-testid="vote-up-btn"
    >
      <ArrowBigUp size={small ? 16 : 22} className={myVote === "up" ? "fill-[#00FFAA]/40" : ""} />
    </button>
    <span className={`font-heading font-black ${small ? "text-[10px]" : "text-sm"} ${score > 0 ? "text-[#00FFAA]" : score < 0 ? "text-[#FF6B6B]" : "text-white/50"}`}>
      {score}
    </span>
    <button
      onClick={(e) => { e.stopPropagation(); onVote(myVote === "down" ? "clear" : "down"); }}
      className={`p-0.5 rounded transition-colors ${myVote === "down" ? "text-[#FF6B6B]" : "text-white/30 hover:text-[#FF6B6B]"}`}
      data-testid="vote-down-btn"
    >
      <ArrowBigDown size={small ? 16 : 22} className={myVote === "down" ? "fill-[#FF6B6B]/40" : ""} />
    </button>
  </div>
);

/* Comment tree flattened to a list (depth-indented) — NO recursive JSX components,
   which break the dev-mode babel visual-edits plugin. */
const flattenComments = (comments) => {
  const map = {};
  const roots = [];
  (comments || []).forEach((c) => { map[c.id] = { ...c, children: [] }; });
  (comments || []).forEach((c) => {
    if (c.parent_id && map[c.parent_id]) map[c.parent_id].children.push(map[c.id]);
    else roots.push(map[c.id]);
  });
  const out = [];
  const walk = (node, depth) => {
    out.push({ node, depth });
    node.children.forEach((child) => walk(child, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return out;
};

const CommentRow = ({ node, depth, onVote, imgsFallback }) => (
  <div style={{ marginLeft: Math.min(depth, 5) * 20 }} className={depth > 0 ? "border-l border-white/10 pl-3" : ""}>
    <div className="flex items-start gap-2.5 py-2" data-testid="forum-comment">
      <VoteColumn small score={node.score} myVote={node.my_vote} onVote={(dir) => onVote(node.id, dir)} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-[#00F5FF] mb-0.5 flex items-center gap-1.5">
          <img src={node.avatar_img || imgsFallback} alt="" className="w-4 h-4 rounded-full object-contain bg-[#00F5FF]/10" loading="lazy" />
          {node.author_name} <span className="text-white/25 font-normal">· {timeAgo(node.created_at)}</span>
        </p>
        <p className="text-xs text-white/80 leading-relaxed">{node.text}</p>
      </div>
    </div>
  </div>
);

const TribeCard = ({ tribe, lang, tt, onJoin, onLeave, onEnter, busySlug }) => {
  const name = tribe.name[lang] || tribe.name.en;
  const tags = tribe.tags[lang] || tribe.tags.en;
  const busy = busySlug === tribe.slug;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid={`tribe-card-${tribe.slug}`}
      className="relative bg-[#0F0F1A]/70 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-white/25 transition-colors flex flex-col"
    >
      {/* Banner */}
      <div
        className="relative h-24 flex items-end justify-between px-5 overflow-hidden"
        style={{ background: `linear-gradient(120deg, ${tribe.color}30, transparent 60%), radial-gradient(circle at 85% 30%, ${tribe.color}45, transparent 65%)` }}
      >
        <span
          className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border mb-3"
          style={{ color: tribe.color, borderColor: `${tribe.color}55`, backgroundColor: `${tribe.color}12` }}
        >
          <Users size={10} /> {tribe.members} {tt.members}
        </span>
        <img
          src={tribe.mascot}
          alt={name}
          loading="lazy"
          className="h-20 w-auto object-contain -mb-1 drop-shadow-[0_0_18px_rgba(176,38,255,0.5)]"
        />
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-black text-white text-sm mb-2 flex items-center gap-2 flex-wrap">
          {name}
          {tribe.is_default && (
            <span className="text-[8px] font-bold uppercase tracking-widest text-[#00F5FF] bg-[#00F5FF]/10 border border-[#00F5FF]/40 px-2 py-0.5 rounded-full" data-testid="pueblo-badge">
              🏘️ {tt.puebloBadge}
            </span>
          )}
          {tribe.is_member && !tribe.is_default && (
            <span className="text-[8px] font-bold uppercase tracking-widest text-[#00FFAA] bg-[#00FFAA]/10 border border-[#00FFAA]/40 px-2 py-0.5 rounded-full" data-testid={`tribe-member-badge-${tribe.slug}`}>
              {tt.memberBadge}
            </span>
          )}
        </h3>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tags.map((tag) => (
            <span key={tag} className="text-[9px] font-bold text-white/50 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
              [{tag}]
            </span>
          ))}
        </div>
        {tribe.is_member && (
          <p className="text-[9px] font-bold text-[#00FFAA] mb-3 flex items-center gap-1.5"><Zap size={10} className="fill-[#00FFAA]/40" /> {tt.boost}</p>
        )}
        <div className="mt-auto flex flex-col gap-2">
          {tribe.is_member ? (
            <>
              <button
                data-testid={`tribe-enter-${tribe.slug}`}
                onClick={() => onEnter(tribe)}
                className="w-full py-3 rounded-full text-[10px] font-bold uppercase tracking-widest text-white transition-all"
                style={{ background: `linear-gradient(90deg, ${tribe.color}CC, ${tribe.color}88)`, boxShadow: `0 0 18px ${tribe.color}55` }}
              >
                {tt.enter}
              </button>
              {!tribe.is_default && (
                <button
                  data-testid={`tribe-leave-${tribe.slug}`}
                  onClick={() => onLeave(tribe)}
                  disabled={busy}
                  className="w-full py-2.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-white/40 border border-white/10 hover:text-white/70 hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  {tt.leave}
                </button>
              )}
            </>
          ) : (
            <button
              data-testid={`tribe-join-${tribe.slug}`}
              onClick={() => onJoin(tribe)}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest text-white bg-[#B026FF] border border-[#D080FF]/40 shadow-[0_0_18px_rgba(176,38,255,0.4)] hover:shadow-[0_0_28px_rgba(176,38,255,0.65)] hover:-translate-y-0.5 transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />}
              {busy ? tt.joining : tt.join}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const Tribes = ({ t, lang }) => {
  const tt = t.tribes;
  const w = t.wall;
  const [tribes, setTribes] = useState(null);
  const [busySlug, setBusySlug] = useState(null);
  const [active, setActive] = useState(null); // tribe being viewed
  const [view, setView] = useState("board"); // board | wall
  const [sort, setSort] = useState("hot");
  const [threads, setThreads] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [feed, setFeed] = useState(null);
  const [replyingId, setReplyingId] = useState(null);

  const loadForum = useCallback(async (slug, s) => {
    setThreads(null);
    try {
      const { data } = await api.get(`/tribes/${slug}/forum?sort=${s}`);
      setThreads(data.threads);
    } catch {
      setThreads([]);
    }
  }, []);

  const openThread = async (threadId) => {
    setActiveThread({ loading: true });
    try {
      const { data } = await api.get(`/forum/threads/${threadId}`);
      setActiveThread(data);
    } catch {
      setActiveThread(null);
      toast.error("Error");
    }
  };

  const voteThread = async (threadId, direction) => {
    try {
      const { data } = await api.post(`/forum/threads/${threadId}/vote`, { direction });
      setThreads((ts) => (ts || []).map((t) => (t.id === threadId ? { ...t, score: data.score, my_vote: data.my_vote } : t)));
      setActiveThread((t) => (t && t.id === threadId ? { ...t, score: data.score, my_vote: data.my_vote } : t));
    } catch {
      // silent
    }
  };

  const voteComment = async (threadId, commentId, direction) => {
    try {
      const { data } = await api.post(`/forum/threads/${threadId}/comments/${commentId}/vote`, { direction });
      setActiveThread((t) => t && {
        ...t,
        comments: (t.comments || []).map((c) => (c.id === commentId ? { ...c, score: data.score, my_vote: data.my_vote } : c)),
      });
    } catch {
      // silent
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tribes");
      setTribes(data);
    } catch {
      setTribes([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const join = async (tribe) => {
    setBusySlug(tribe.slug);
    try {
      const { data } = await api.post(`/tribes/${tribe.slug}/join`);
      setTribes((ts) => ts.map((x) => (x.slug === data.slug ? data : x)));
      toast.success(tt.joined);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusySlug(null);
    }
  };

  const leave = async (tribe) => {
    setBusySlug(tribe.slug);
    try {
      const { data } = await api.post(`/tribes/${tribe.slug}/leave`);
      setTribes((ts) => ts.map((x) => (x.slug === data.slug ? data : x)));
      toast.info(tt.left);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusySlug(null);
    }
  };

  const enter = async (tribe) => {
    setActive(tribe);
    setView("board");
    setActiveThread(null);
    setFeed(null);
    loadForum(tribe.slug, "hot");
    setSort("hot");
  };

  const loadWall = async (tribe) => {
    setFeed(null);
    try {
      const { data } = await api.get(`/tribes/${tribe.slug}/feed`);
      setFeed(data.posts);
    } catch {
      setFeed([]);
    }
  };

  const like = async (post) => {
    try {
      const { data } = await api.post(`/wall/posts/${post.id}/like`);
      setFeed((ps) => ps.map((p) => (p.id === post.id ? { ...p, liked_by_me: data.liked, likes: data.likes, popularity: data.popularity } : p)));
    } catch {
      // silent
    }
  };

  const agentReply = async (post) => {
    setReplyingId(post.id);
    try {
      const { data } = await api.post(`/wall/posts/${post.id}/comment`, { lang });
      setFeed((ps) => ps.map((p) => (p.id === post.id ? { ...p, comments: [...(p.comments || []), data.comment], popularity: data.popularity } : p)));
      toast.success(w.commentAdded);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setReplyingId(null);
    }
  };

  /* ===== Tribe community wall view ===== */
  if (active) {
    const name = active.name[lang] || active.name.en;

    /* Flat pre-computed sections (avoids deep JSX nesting that overflows the compiler) */
    const spinner = (
      <div className="flex justify-center py-16 text-white/40"><Loader2 size={24} className="animate-spin" /></div>
    );

    let boardContent = spinner;
    if (threads !== null && threads.length === 0) {
      boardContent = (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-3xl" data-testid="forum-empty">
          <MessageSquare size={32} className="mx-auto text-white/20 mb-3" />
          <p className="text-sm text-white/40">{tt.emptyBoard}</p>
        </div>
      );
    } else if (threads !== null) {
      boardContent = (
        <div className="space-y-3" data-testid="forum-thread-list">
          {threads.map((t) => (
            <div
              key={t.id}
              data-testid={`forum-thread-${t.id}`}
              onClick={() => openThread(t.id)}
              className="flex items-start gap-3 bg-[#0F0F1A]/70 border border-white/10 rounded-2xl p-4 cursor-pointer hover:border-[#B026FF]/35 transition-colors"
            >
              <VoteColumn score={t.score} myVote={t.my_vote} onVote={(dir) => voteThread(t.id, dir)} />
              <div className="min-w-0 flex-1">
                <h4 className="font-heading font-black text-sm text-white leading-snug mb-1">{t.title}</h4>
                <p className="text-[11px] text-white/50 leading-relaxed line-clamp-2 mb-2">{t.body}</p>
                <p className="text-[10px] text-white/35 flex items-center gap-1.5 flex-wrap">
                  <img src={t.avatar_img || active.mascot} alt="" className="w-4 h-4 rounded-full object-contain bg-[#B026FF]/10" loading="lazy" />
                  u/{t.author_name} · {timeAgo(t.created_at)}
                  <span className="inline-flex items-center gap-1 text-[#00F5FF] ml-2"><MessageSquare size={10} /> {t.comments_count} {tt.commentsLbl}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    let wallContent = spinner;
    if (feed !== null && feed.length === 0) {
      wallContent = (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-3xl" data-testid="tribe-feed-empty">
          <Tent size={32} className="mx-auto text-white/20 mb-3" />
          <p className="text-sm text-white/40">{tt.emptyFeed}</p>
        </div>
      );
    } else if (feed !== null) {
      wallContent = (
        <div className="space-y-5" data-testid="tribe-feed-list">
          {feed.map((p) => (
            <PostCard key={p.id} post={p} w={w} lang={lang} onLike={like} onAgentReply={agentReply} replyingId={replyingId} />
          ))}
        </div>
      );
    }

    const tabsRow = (
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="tribe-view-tabs">
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {[
            { id: "board", label: tt.boardTab, icon: LayoutList },
            { id: "wall", label: tt.wallTab, icon: Newspaper },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tribe-tab-${id}`}
              onClick={() => { setView(id); if (id === "wall" && feed === null) loadWall(active); }}
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full transition-colors ${
                view === id ? "bg-[#B026FF] text-white" : "text-white/50 hover:text-white"
              }`}
            >
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
        {view === "board" && (
          <div className="flex gap-1 ml-auto" data-testid="forum-sort-pills">
            {[
              { id: "hot", label: tt.sortHot, icon: Flame },
              { id: "top", label: tt.sortTop, icon: TrendingUp },
              { id: "new", label: tt.sortNew, icon: Clock },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`forum-sort-${id}`}
                onClick={() => { setSort(id); loadForum(active.slug, id); }}
                className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                  sort === id ? "text-[#FFA726] border-[#FFA726]/50 bg-[#FFA726]/10" : "text-white/40 border-white/10 hover:text-white"
                }`}
              >
                <Icon size={10} /> {label}
              </button>
            ))}
          </div>
        )}
      </div>
    );

    let threadDetail = null;
    if (activeThread && activeThread.loading) {
      threadDetail = spinner;
    } else if (activeThread) {
      threadDetail = (
        <div data-testid="forum-thread-view">
          <button
            data-testid="forum-back-board-btn"
            onClick={() => setActiveThread(null)}
            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-white mb-4"
          >
            <ArrowLeft size={12} /> {tt.backBoard}
          </button>
          <div className="flex items-start gap-3 bg-[#0F0F1A]/70 border border-white/10 rounded-3xl p-5 mb-4">
            <VoteColumn score={activeThread.score} myVote={activeThread.my_vote} onVote={(dir) => voteThread(activeThread.id, dir)} />
            <div className="min-w-0 flex-1">
              <h3 className="font-heading font-black text-base text-white mb-2" data-testid="forum-thread-title">{activeThread.title}</h3>
              <p className="text-xs text-white/75 leading-relaxed mb-3">{activeThread.body}</p>
              <p className="text-[10px] text-white/35 flex items-center gap-1.5">
                <img src={activeThread.avatar_img || active.mascot} alt="" className="w-4 h-4 rounded-full object-contain bg-[#B026FF]/10" loading="lazy" />
                u/{activeThread.author_name} · {timeAgo(activeThread.created_at)} · <MessageSquare size={10} /> {activeThread.comments_count}
              </p>
            </div>
          </div>
          <div className="bg-[#0F0F1A]/50 border border-white/5 rounded-3xl p-4" data-testid="forum-comments-tree">
            {flattenComments(activeThread.comments).map(({ node, depth }) => (
              <CommentRow key={node.id} node={node} depth={depth} onVote={(cid, dir) => voteComment(activeThread.id, cid, dir)} imgsFallback={active.mascot} />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-2xl" data-testid="tribe-wall-view">
        <button
          data-testid="tribe-back-btn"
          onClick={() => { setActive(null); setFeed(null); setThreads(null); setActiveThread(null); load(); }}
          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-white mb-5"
        >
          <ArrowLeft size={13} /> {tt.back}
        </button>
        <div
          className="flex items-center gap-4 rounded-3xl border border-white/10 px-6 py-5 mb-6 overflow-hidden relative"
          style={{ background: `linear-gradient(120deg, ${active.color}20, transparent 70%)` }}
        >
          <img src={active.mascot} alt={name} className="h-16 w-auto object-contain drop-shadow-[0_0_16px_rgba(176,38,255,0.5)]" />
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-bold">{tt.wallOf}</p>
            <h2 className="font-heading font-black text-lg text-white">{name}</h2>
            <p className="text-[10px] font-bold mt-1" style={{ color: active.color }}>
              <Users size={10} className="inline mr-1" />{active.members} {tt.members} · <Zap size={10} className="inline mr-0.5" /> {tt.boost}
            </p>
          </div>
        </div>

        {threadDetail}
        {!activeThread && tabsRow}
        {!activeThread && view === "board" && boardContent}
        {!activeThread && view === "wall" && wallContent}
      </div>
    );
  }

  /* ===== Tribe directory ===== */
  return (
    <div className="max-w-4xl" data-testid="tribe-directory">
      <p className="text-sm text-[#A0A0B0] -mt-4 mb-8">{tt.sub}</p>
      {tribes === null ? (
        <div className="flex justify-center py-16 text-white/40"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {tribes.map((tribe) => (
              <TribeCard key={tribe.slug} tribe={tribe} lang={lang} tt={tt} onJoin={join} onLeave={leave} onEnter={enter} busySlug={busySlug} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
